import type { WarningEntry } from 'components/WarningIndicator';

import { getOrderedQuantity, SupplyOrderProductFormValue } from './supplyOrderFormValues';

/**
 * Tout ce qui cloche sur une ligne de l'achat.
 *
 * `critical` : le fournisseur refusera la ligne, ou on achète ce qu'on ne devrait pas — à corriger
 * avant d'envoyer la commande. `warning` : à vérifier, l'achat part quand même. `info` : une
 * contrainte du produit, rappelée même quand elle est respectée.
 *
 * Les quantités viennent du FORMULAIRE : la liste se recalcule à chaque frappe sur les colis.
 */
export function getSupplyOrderProductWarnings({
    product,
    supplierMinQuantity,
}: {
    product: SupplyOrderProductFormValue;
    /** Minimum imposé par le fournisseur pour ce produit (ses conditions de commande). */
    supplierMinQuantity?: number;
}): WarningEntry[] {
    const warnings: WarningEntry[] = [];
    const orderedQuantity = getOrderedQuantity(product);

    if (product.isToBeArchived) {
        warnings.push({
            level: 'critical',
            message: 'Ce produit est marqué « À sortir » et ne devrait plus être commandé.',
        });
    }

    if (product.supplierShortageInfo?.inShortage) {
        const { returnDate } = product.supplierShortageInfo;
        warnings.push({
            level: 'critical',
            message: `Ce produit est en rupture chez le fournisseur${returnDate ? ` jusqu'au ${returnDate}` : ''} et ne devrait pas être commandé.`,
        });
    }

    if (supplierMinQuantity) {
        warnings.push(
            getMinQuantityNotice({
                subject: 'Le minimum de commande imposé par le fournisseur pour ce produit',
                minQuantity: supplierMinQuantity,
                orderedQuantity,
            }),
        );
    }

    if (product.minSupplierOrderQuantity) {
        warnings.push(
            getMinQuantityNotice({
                subject: 'La quantité minimale de commande de ce produit',
                minQuantity: product.minSupplierOrderQuantity,
                orderedQuantity,
            }),
        );
    }

    if (product.isAtSoldOutRisk) {
        warnings.push({ level: 'warning', message: 'Ce produit risque la rupture de stock.' });
    }

    const requestedQuantity = product.quantityGenerated + product.quantityGeneratedForMenus;
    if (orderedQuantity < requestedQuantity) {
        warnings.push({
            level: 'warning',
            message: `La quantité commandée ne couvre pas les besoins calculés (menus ${product.quantityGeneratedForMenus} + prédictions ${product.quantityGenerated}).`,
        });
    }

    if (
        product.confirmedQuantity !== null &&
        product.conditionning &&
        !Number.isInteger(product.confirmedQuantity / product.conditionning)
    ) {
        warnings.push({
            level: 'warning',
            message: `La quantité confirmée par le fournisseur ne tombe pas juste sur le colisage (${product.conditionning}), soit ${
                Math.round((product.confirmedQuantity / product.conditionning) * 100) / 100
            } colis.`,
        });
    }

    return warnings;
}

/**
 * Un minimum de commande se lit TOUJOURS : c'est une contrainte du produit, pas un accident, et on la
 * veut sous les yeux au moment de choisir les colis. Il ne devient un problème que sur une ligne
 * réellement commandée — à zéro colis, le produit n'est pas de l'achat.
 */
function getMinQuantityNotice({
    subject,
    minQuantity,
    orderedQuantity,
}: {
    subject: string;
    minQuantity: number;
    orderedQuantity: number;
}): WarningEntry {
    const sentence = `${subject} est de ${minQuantity} unité(s)`;

    return orderedQuantity && minQuantity > orderedQuantity
        ? { level: 'critical', message: `${sentence}, la commande n'en porte que ${orderedQuantity}.` }
        : { level: 'info', message: `${sentence}.` };
}
